import type { SponsoredBrandAsset } from "@adpilot/shared";
import { IconPhoto, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";

export function AssetPicker({
  assets,
  loading,
  unavailable,
  value,
  onValueChange,
}: {
  assets: SponsoredBrandAsset[];
  loading: boolean;
  unavailable: boolean;
  value: string | null;
  onValueChange: (assetId: string | null) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [draftValue, setDraftValue] = useState(value);
  const selected = assets.find((asset) => asset.assetId === value);
  const visible = assets.filter((asset) =>
    [asset.name, asset.assetId, ...asset.searchTerms].some((candidate) =>
      candidate.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
    ),
  );
  const boundedVisible = visible.slice(0, 100);

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setDraftValue(value);
      setQuery("");
    }
    setOpen(nextOpen);
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={loading || unavailable}
        onClick={() => handleOpenChange(true)}
      >
        {loading ? <Spinner /> : <IconPhoto />}
        {selected?.name ?? t("sd.creative.chooseAssets")}
      </Button>
      {unavailable ? (
        <p className="text-sm text-warning-foreground">{t("sd.creative.assetsUnavailable")}</p>
      ) : null}
      {!loading && !unavailable && assets.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("sd.creative.noEligibleAssets")}</p>
      ) : null}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("sd.creative.assetLibrary")}</DialogTitle>
          </DialogHeader>
          <DialogPanel className="flex flex-col gap-4">
            <InputGroup>
              <InputGroupAddon>
                <IconSearch />
              </InputGroupAddon>
              <InputGroupInput
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("sd.creative.assetSearch")}
              />
            </InputGroup>
            <div className="grid max-h-[28rem] grid-cols-3 gap-3 overflow-y-auto">
              {boundedVisible.map((asset) => (
                <button
                  key={asset.assetId}
                  type="button"
                  aria-pressed={draftValue === asset.assetId}
                  className="overflow-hidden rounded-lg border border-line text-left aria-pressed:border-primary aria-pressed:ring-2 aria-pressed:ring-primary/20"
                  onClick={() => setDraftValue(asset.assetId)}
                >
                  <div className="aspect-video bg-muted">
                    {asset.url ? (
                      <img alt="" src={asset.url} className="size-full object-contain" />
                    ) : null}
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-medium">{asset.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {(asset.sizeBytes / 1024).toFixed(1)} KB · {asset.width}*{asset.height}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            {visible.length > boundedVisible.length ? (
              <p className="text-sm text-muted-foreground">
                {t("sd.creative.assetTruncated", {
                  shown: boundedVisible.length,
                  total: visible.length,
                })}
              </p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              disabled={!draftValue}
              onClick={() => {
                onValueChange(draftValue);
                handleOpenChange(false);
              }}
            >
              {t("sd.creative.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
